import { useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import {
  REMOVAL_STEPS,
  REMOVAL_FOLK_REMEDIES,
  REMOVAL_TOOLS,
  type RemovalStep,
} from "@/lib/removal";
import { WarningCard } from "@/components/page-shell";
import { cn } from "@/lib/utils";

/** A plain list with the tick's spot as the bullet (same mark as the risk snapshot's). */
function DotList({ items, className }: { items: readonly string[]; className?: string }) {
  return (
    <ul className={cn("space-y-1.5", className)}>
      {items.map((t) => (
        <li key={t} className="flex gap-2.5">
          <span
            aria-hidden
            className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-rust [print-color-adjust:exact]"
          />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * One step as a printed guide sets it: a large numeral in the headline face, the
 * instruction beside it, and the mistake to avoid as a short note in the warning colour.
 * The note used to carry an amber triangle; the colour and the words ("Don't…",
 * "Never…") already say it.
 *
 * Spans rather than headings because the interactive version sits inside a <label>,
 * which only takes phrasing content.
 */
function StepRow({
  step,
  n,
  muted = false,
  mark,
}: {
  step: RemovalStep;
  n: number;
  muted?: boolean;
  mark: ReactNode;
}) {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          "tabular font-display text-[2rem] leading-none transition-colors sm:text-[2.375rem] print:text-[1.625rem]",
          muted ? "text-muted-foreground" : "text-rust-ink",
        )}
      >
        {n}
      </span>
      <span className="min-w-0">
        <span className="block font-display text-xl font-medium leading-snug text-foreground print:text-[1.0625rem]">
          {step.title}
        </span>
        <span className="mt-1.5 block text-[0.9375rem] leading-relaxed text-muted-foreground print:mt-0.5 print:text-[0.8125rem] print:leading-snug">
          {step.body}
        </span>
        {step.avoid && (
          <span className="mt-2 block text-sm font-medium leading-relaxed text-destructive print:mt-0.5 print:text-[0.8125rem] print:leading-snug">
            {step.avoid}
          </span>
        )}
      </span>
      {mark}
    </>
  );
}

const ROW =
  "grid grid-cols-[2.25rem_1fr_auto] gap-x-3 py-5 sm:grid-cols-[3.25rem_1fr_auto] sm:gap-x-5 print:py-2.5";

/**
 * The six steps as something to read or print: no inputs, and a drawn box beside each
 * step to tick on paper. Used by the printable action plan.
 */
export function RemovalStepList({ className }: { className?: string }) {
  return (
    <ol className={cn("border-t border-rule/70", className)}>
      {REMOVAL_STEPS.map((step, i) => (
        <li key={step.id} className={cn(ROW, "border-b border-border")}>
          <StepRow
            step={step}
            n={i + 1}
            mark={
              <span
                aria-hidden
                className="mt-1 h-5 w-5 shrink-0 rounded-[2px] border-[1.5px] border-foreground/60"
              />
            }
          />
        </li>
      ))}
    </ol>
  );
}

/** The folk remedies the CDC warns against, as a margin note rather than a filled box. */
export function FolkRemedies({ headingLevel = "h2" }: { headingLevel?: "h2" | "h3" }) {
  const Heading = headingLevel;
  return (
    <WarningCard>
      <Heading className="font-sans text-[0.9375rem] font-semibold tracking-normal text-foreground">
        Don&rsquo;t try to make it back out
      </Heading>
      <p className="mt-1">
        These are the methods people are most often told to use, and each one makes an attached tick
        more likely to release fluid into the bite:
      </p>
      <DotList items={REMOVAL_FOLK_REMEDIES} className="mt-2 sm:columns-2 sm:gap-8" />
    </WarningCard>
  );
}

/**
 * The removal procedure as something you work through, not a grid you read.
 *
 * The old page laid six steps out in a two-column grid, so on a desktop the reading
 * order ran 1-2 / 3-4 / 5-6 — a numbered sequence presented out of sequence. These are
 * a single ruled column with a box each to tick off, because the person reading them is
 * doing them one at a time with one hand. The whole row is the tap target.
 */
export function RemovalSteps({ onDone }: { onDone: () => void }) {
  const [done, setDone] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allDone = done.size === REMOVAL_STEPS.length;

  return (
    <div className="space-y-10">
      <section aria-labelledby="removal-tools">
        <h2 id="removal-tools" className="label-caps text-muted-foreground">
          What you need
        </h2>
        <DotList items={REMOVAL_TOOLS} className="mt-3 text-[0.9375rem] text-foreground" />
        <p className="measure mt-3 text-sm leading-relaxed text-muted-foreground">
          No tweezers to hand? It is still better to remove it now with clean fingers protected by
          tissue or a glove than to leave it attached while you look for a better tool.
        </p>
      </section>

      <section aria-labelledby="removal-steps">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 id="removal-steps" className="label-caps text-muted-foreground">
            Step by step
          </h2>
          <p className="no-print text-xs text-muted-foreground">Tick each one off as you go.</p>
        </div>
        <ol className="mt-3 border-t border-rule/70">
          {REMOVAL_STEPS.map((step, i) => {
            const checked = done.has(step.id);
            return (
              <li key={step.id} className="border-b border-border">
                <label className={cn(ROW, "group cursor-pointer")}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(step.id)}
                    className="peer sr-only"
                  />
                  <StepRow
                    step={step}
                    n={i + 1}
                    muted={checked}
                    mark={
                      <span
                        aria-hidden
                        className={cn(
                          "mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-[3px] border-[1.5px] transition-colors",
                          "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring",
                          checked
                            ? "border-primary bg-primary text-primary-foreground [print-color-adjust:exact]"
                            : "border-foreground/50 bg-card group-hover:border-foreground",
                        )}
                      >
                        {checked && <Check className="h-4 w-4" strokeWidth={2.5} />}
                      </span>
                    }
                  />
                </label>
              </li>
            );
          })}
        </ol>
      </section>

      <FolkRemedies />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <button
          type="button"
          onClick={onDone}
          className="inline-flex min-h-11 items-center rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-dark"
        >
          {allDone ? "Done. Log this bite" : "It's out. Log this bite"}
        </button>
        <p className="text-sm text-muted-foreground">
          {done.size > 0 && !allDone
            ? `${done.size} of ${REMOVAL_STEPS.length} done`
            : "Takes about a minute. Nothing leaves your phone."}
        </p>
      </div>
    </div>
  );
}
